import FormatCurrency from 'components/Common/FormatCurrency'
import Modal from 'components/Luxkit/Modal/Modal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import FormatPercent from 'components/utils/Formatters/FormatPercent'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import React, { ComponentProps, useCallback } from 'react'

type InheritedModalProps = Pick<ComponentProps<typeof Modal>, 'primaryActionText'>

interface Props extends InheritedModalProps {
  paymentScheduleTemplate: Tours.TourPaymentScheduleTemplate
}

export type TourDepositInfoModalResult = 'primary-action' | undefined

function TourDepositInfoModal({ paymentScheduleTemplate, primaryActionText }: Props) {
  const modalElement = useModalElementContext<TourDepositInfoModalResult>()

  const handlePrimaryClick = useCallback(() => {
    modalElement.resolve('primary-action')
  }, [modalElement])

  const deposit = paymentScheduleTemplate.schedules[0]

  return <Modal
    title="How it works"
    primaryActionText={primaryActionText}
    onPrimaryActionClick={primaryActionText ? handlePrimaryClick : undefined}
  >
    <Group direction="vertical" gap={16}>
      <div>
        <BodyText variant="medium" colour="neutral-two">
          Through Payment Schedules, we provide you flexibility to secure your tour with {' '}
          {deposit.amountType === 'fixed_amount' && <FormatCurrency value={deposit.amountValue} />}
          {deposit.amountType === 'percentage' && <FormatPercent value={deposit.amountValue} />} upfront, and pay off the balance across multiple payments.
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">Pay deposit now</Heading>
        <BodyText variant="medium" colour="neutral-two">
          Secure your dates by paying{' '}
          {deposit.amountType === 'fixed_amount' && <FormatCurrency value={deposit.amountValue} />}
          {deposit.amountType === 'percentage' && <FormatPercent value={deposit.amountValue} />}
          {' '}of your booking today. Add ons such as  {LUXURY_PLUS.PROGRAM_NAME} memberships, optional experiences and travel protection must be paid upfront in addition to the{' '}
          {deposit.amountType === 'fixed_amount' && <FormatCurrency value={deposit.amountValue} />}
          {deposit.amountType === 'percentage' && <FormatPercent value={deposit.amountValue} />} deposit.
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">Pay the rest later</Heading>
        <BodyText variant="medium" colour="neutral-two">
          Payments will be automatically charged to your original payment method on each due date. Don't worry, we'll send you a reminder email 3 to 5 days before your payment is due.
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">Service Fee</Heading>
        <BodyText variant="medium" colour="neutral-two">
          The service fee paid as part of a Payment Schedule is non-refundable, even if cancellation occurs within 7 days of purchase.
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">Cancellation policy</Heading>
        <BodyText variant="medium" colour="neutral-two">
          All deposits, service fee, and payments are non-refundable.
          <br /><br />
          Once you have made a payment as part of your Payment Schedule, that payment is non-refundable. Failure to complete any payment will result in the cancellation of your booking, and you will forfeit the entire amount you have paid.
          <br /><br />
          We may allow a cancellation for credit due to exceptional circumstances (and at our sole discretion). Where a cancellation/credit is not otherwise permitted and you still wish cancel your booking, an additional service fee of A$300 per person will apply (above and beyond the existing fees already paid pursuant to the payment schedules).
        </BodyText>
      </div>
    </Group>
  </Modal>
}

export default TourDepositInfoModal
